/* ==========================================================================
   KASHTAN — Motion engine
   --------------------------------------------------------------------------
   SAFETY FIRST. An earlier version hid content with opacity:0 and relied
   entirely on IntersectionObserver to bring it back. When the observer did not
   fire — the Shopify theme editor, lazy images shifting layout, a throttled or
   backgrounded tab — content stayed invisible forever. Images vanished.

   The rules now:
     1. Nothing is hidden until JS has proved it can reveal it again.
     2. A hard failsafe reveals EVERYTHING after a timeout, no matter what.
     3. The Shopify theme editor never animates — merchants see final layout.
     4. Anything already on screen at init reveals immediately, not on scroll.
     5. window.load re-checks, because images change layout after DOMContentLoaded.

   If you change nothing else in this file, keep those five.
   ========================================================================== */
(function () {
  'use strict';

  if (window.KashtanMotion) return;

  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var supported = 'IntersectionObserver' in window && 'requestAnimationFrame' in window;
  var inEditor = !!(window.Shopify && window.Shopify.designMode);

  // Global off-switch, set from theme settings (see snippets/kashtan-settings.liquid)
  var enabled = document.documentElement.getAttribute('data-k-motion') !== 'off';

  // Animate only when it is safe AND wanted
  var animate = enabled && !reduced && !inEditor && supported;

  var FAILSAFE_MS = 2600;

  var K = {
    reduced: reduced,
    inEditor: inEditor,
    animate: animate,
    observer: null,
    parallaxItems: [],
    ticking: false,
    revealedAll: false
  };

  /* ------------------------------------------------------------ reveal all */

  function revealAll(scope) {
    scope = scope || document;
    scope.querySelectorAll('[data-k-reveal]').forEach(function (el) {
      el.classList.add('is-in');
      if (el.hasAttribute('data-k-count')) countUp(el, 0);
    });
    scope.querySelectorAll('.k-words').forEach(function (el) { el.classList.add('is-in'); });
    scope.querySelectorAll('[data-k-split]').forEach(function (el) { el.classList.add('is-in'); });
  }

  /* ---------------------------------------------------------------- reveal */

  function reveal(el) {
    var delay = parseInt(el.getAttribute('data-k-delay') || '0', 10);
    var parent = el.closest('[data-k-stagger]');

    if (parent && !el.hasAttribute('data-k-delay')) {
      var step = parseInt(parent.getAttribute('data-k-stagger') || '90', 10);
      var sibs = parent.querySelectorAll('[data-k-reveal]');
      var index = Array.prototype.indexOf.call(sibs, el);
      if (index > -1) delay = index * step;
    }

    if (delay) el.style.setProperty('--k-d', delay + 'ms');
    el.classList.add('is-in');

    if (el.hasAttribute('data-k-count')) countUp(el, delay);
  }

  /* ------------------------------------------------------------ split text */

  function split(el) {
    if (el.dataset.kSplitDone) return;
    el.dataset.kSplitDone = '1';

    var step = parseInt(el.getAttribute('data-k-split') || '55', 10) || 55;
    var nodes = Array.prototype.slice.call(el.childNodes);
    var wrap = document.createElement('span');
    wrap.className = 'k-words';
    var i = 0;

    nodes.forEach(function (node) {
      if (node.nodeType === 3) {
        node.textContent.split(/(\s+)/).forEach(function (chunk) {
          if (!chunk) return;
          if (/^\s+$/.test(chunk)) { wrap.appendChild(document.createTextNode(' ')); return; }
          var word = document.createElement('span');
          word.className = 'k-word';
          var inner = document.createElement('span');
          inner.textContent = chunk;
          inner.style.setProperty('--k-wd', i * step + 'ms');
          word.appendChild(inner);
          wrap.appendChild(word);
          i++;
        });
      } else {
        wrap.appendChild(node.cloneNode(true));
      }
    });

    el.innerHTML = '';
    el.appendChild(wrap);
    el._kWords = wrap;
  }

  /* --------------------------------------------------------------- counter */

  function countUp(el, delay) {
    if (el.dataset.kCounted) return;
    var target = parseFloat(el.getAttribute('data-k-count'));
    if (isNaN(target)) return;
    el.dataset.kCounted = '1';

    var raw = el.getAttribute('data-k-count');
    var decimals = (raw.split('.')[1] || '').length;
    var duration = parseInt(el.getAttribute('data-k-count-dur') || '1400', 10);
    var prefix = el.getAttribute('data-k-count-prefix') || '';
    var suffix = el.getAttribute('data-k-count-suffix') || '';

    function show(v) { el.textContent = prefix + v.toFixed(decimals) + suffix; }

    if (!K.animate) { show(target); return; }

    setTimeout(function () {
      var start = null;
      function frame(now) {
        if (start === null) start = now;
        var p = Math.min((now - start) / duration, 1);
        var eased = p === 1 ? 1 : 1 - Math.pow(2, -10 * p);
        show(target * eased);
        if (p < 1) requestAnimationFrame(frame);
      }
      requestAnimationFrame(frame);
    }, delay || 0);
  }

  /* -------------------------------------------------------------- parallax */

  function runParallax() {
    var vh = window.innerHeight;
    K.parallaxItems.forEach(function (item) {
      var rect = item.el.getBoundingClientRect();
      if (rect.bottom < -200 || rect.top > vh + 200) return;
      var progress = (rect.top + rect.height / 2 - vh / 2) / vh;
      item.el.style.transform = 'translate3d(0,' + (-progress * item.amount * 100).toFixed(2) + 'px,0)';
    });
    K.ticking = false;
  }

  function onScroll() {
    if (K.ticking || !K.parallaxItems.length) return;
    K.ticking = true;
    requestAnimationFrame(runParallax);
  }

  /* --------------------------------------------------------------- marquee */

  function marquee(el) {
    if (el.dataset.kMarqueeDone) return;
    el.dataset.kMarqueeDone = '1';
    var track = el.querySelector('.k-marquee__track');
    if (!track) return;

    // Items inside a marquee never use scroll-reveal: the strip is already
    // moving, and a clone would otherwise start invisible off to the right.
    track.querySelectorAll('[data-k-reveal]').forEach(function (n) { n.removeAttribute('data-k-reveal'); });
    track.querySelectorAll('[data-k-stagger]').forEach(function (n) { n.removeAttribute('data-k-stagger'); });

    // Clone until the strip is at least twice the container, so a short list
    // (three products, four photographs) still loops without a gap.
    var copies = 1;
    var need = (el.clientWidth || window.innerWidth) * 2;
    var w = track.scrollWidth || 1;
    while (w * (copies + 1) < need && copies < 8) copies++;
    for (var i = 0; i < copies; i++) {
      var clone = track.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      el.appendChild(clone);
    }

    // Constant speed in px/s (data-k-marquee-speed) rather than a fixed
    // duration, so ten cards move at the same pace as three.
    var speed = parseFloat(el.getAttribute('data-k-marquee-speed'));
    if (speed > 0) {
      var setDur = function () {
        var tw = track.scrollWidth;
        if (tw) el.style.setProperty('--k-marquee-dur', (tw / speed).toFixed(2) + 's');
      };
      setDur();
      if ('ResizeObserver' in window) new ResizeObserver(setDur).observe(track);
      window.addEventListener('load', setDur, { once: true });
    }
  }

  /* ---------------------------------------------- reveal what is on screen */

  function revealVisible(scope) {
    scope = scope || document;
    var vh = window.innerHeight || 0;
    scope.querySelectorAll('[data-k-reveal]:not(.is-in)').forEach(function (el) {
      var r = el.getBoundingClientRect();
      // already on screen, or above it — never wait for a scroll that may not come
      if (r.top < vh * 0.92) reveal(el);
    });
  }

  /* ------------------------------------------------------------------ init */

  function init(scope) {
    scope = scope || document;

    scope.querySelectorAll('[data-k-split]').forEach(split);
    scope.querySelectorAll('[data-k-marquee]').forEach(marquee);

    if (!K.animate) { revealAll(scope); return; }

    scope.querySelectorAll('[data-k-reveal], [data-k-split]').forEach(function (el) {
      if (el.dataset.kObserved) return;
      el.dataset.kObserved = '1';
      K.observer.observe(el);
    });

    scope.querySelectorAll('[data-k-parallax]').forEach(function (el) {
      if (el.dataset.kParallaxDone) return;
      el.dataset.kParallaxDone = '1';
      K.parallaxItems.push({ el: el, amount: parseFloat(el.getAttribute('data-k-parallax')) || 0.1 });
    });

    revealVisible(scope);
    if (K.parallaxItems.length) runParallax();
  }

  function boot() {
    if (!K.animate) {
      // Never hide anything. No k-motion-ready class means the CSS leaves
      // every element at its natural opacity.
      revealAll(document);
      document.querySelectorAll('[data-k-split]').forEach(split);
      document.querySelectorAll('[data-k-marquee]').forEach(marquee);
      revealAll(document);
      return;
    }

    K.observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        if (el.hasAttribute('data-k-reveal')) reveal(el);
        if (el._kWords) el._kWords.classList.add('is-in');
        else if (el.hasAttribute('data-k-split')) el.classList.add('is-in');
        K.observer.unobserve(el);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.01 });

    // Only NOW is it safe to hide things.
    document.documentElement.classList.add('k-motion-ready');

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    init(document);

    // Images change layout after DOMContentLoaded — re-check then.
    window.addEventListener('load', function () { revealVisible(document); });

    // HARD FAILSAFE. Whatever went wrong, the page is readable after this.
    setTimeout(function () {
      if (K.revealedAll) return;
      K.revealedAll = true;
      var hidden = document.querySelectorAll('[data-k-reveal]:not(.is-in)').length;
      if (hidden) revealAll(document);
    }, FAILSAFE_MS);
  }

  K.init = init;
  K.split = split;
  K.revealAll = revealAll;
  window.KashtanMotion = K;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: re-scan and show everything immediately on every change.
  ['shopify:section:load', 'shopify:section:reorder', 'shopify:section:select',
   'shopify:block:select', 'shopify:block:deselect'].forEach(function (evt) {
    document.addEventListener(evt, function (e) {
      var scope = e.target && e.target.querySelectorAll ? e.target : document;
      scope.querySelectorAll('[data-k-split]').forEach(split);
      scope.querySelectorAll('[data-k-marquee]').forEach(marquee);
      revealAll(scope);
    });
  });
})();
